import { useCallback, useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";

import { api } from "../api";
import AudioPlayer from "../components/AudioPlayer";
import ExerciseCard from "../components/ExerciseCard";
import VideoPlayer from "../components/VideoPlayer";
import type {
  HomeworkBlock,
  HomeworkComplete,
  HomeworkStats,
} from "../types";
import { haptic, showConfirm, useMainButton } from "../tg";

type Props = {
  onBack: () => void;
  // Сохранённая позиция (block_id) с прошлого визита. null = первый вход или
  // блок ушёл из ДЗ (untake / dz_required снят админом).
  initialPositionKey: string | null;
  onPositionChange: (key: string | null) => void;
};

// Экран «Дневные». Рендерит один блок целиком (видео + текст + все упражнения)
// — как в уроке. Между блоками — «← Назад / Далее →» bottom-bar.
// Упражнения интерактивны: «Готово» бьёт в /homework/complete, чекбокс «Выполнено»
// дотягивает счётчик до target.
//
// Позиция (активный блок) пробрасывается наверх в App: при возврате на этот
// экран открываем тот же блок. Храним по block_id (стабильнее индекса — список
// может пересобраться при take/untake).
export default function DailyHomework({
  onBack,
  initialPositionKey,
  onPositionChange,
}: Props) {
  const [blocks, setBlocks] = useState<HomeworkBlock[] | null>(null);
  const [stats, setStats] = useState<HomeworkStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [positionInitialized, setPositionInitialized] = useState(false);

  useMainButton(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api<HomeworkBlock[]>("/homework/today"),
      api<HomeworkStats>("/homework/stats"),
    ])
      .then(([t, s]) => {
        if (cancelled) return;
        setBlocks(t);
        setStats(s);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Per-exercise счётчик после /complete или /reset-progress.
  const updateExerciseProgress = useCallback(
    (blockId: string, itemIndex: number, newCount: number) => {
      setBlocks((prev) =>
        prev
          ? prev.map((b) =>
              b.block_id === blockId
                ? {
                    ...b,
                    exercise_progress: {
                      ...b.exercise_progress,
                      [itemIndex]: newCount,
                    },
                  }
                : b,
            )
          : prev,
      );
      api<HomeworkStats>("/homework/stats")
        .then(setStats)
        .catch(() => {});
    },
    [],
  );

  // Юзер убрал блок из Дневных через чекбокс — выкидываем его из списка.
  const onBlockUntake = useCallback((blockId: string) => {
    setBlocks((prev) => (prev ? prev.filter((b) => b.block_id !== blockId) : prev));
    api<HomeworkStats>("/homework/stats")
      .then(setStats)
      .catch(() => {});
  }, []);

  // Восстанавливаем стартовый индекс при первом приходе blocks.
  useEffect(() => {
    if (!blocks || positionInitialized) return;
    if (blocks.length === 0) {
      setPositionInitialized(true);
      return;
    }
    let startIdx = -1;
    if (initialPositionKey) {
      startIdx = blocks.findIndex((b) => b.block_id === initialPositionKey);
    }
    if (startIdx < 0) {
      // Первый невыполненный (в блоке есть exercise с completed_today < target).
      startIdx = blocks.findIndex((b) =>
        b.items.some(
          (it, i) =>
            it.type === "exercise" &&
            (b.exercise_progress[i] ?? 0) < it.repetitions_per_day,
        ),
      );
    }
    if (startIdx < 0) startIdx = 0;
    setCurrentIdx(startIdx);
    setPositionInitialized(true);
  }, [blocks, initialPositionKey, positionInitialized]);

  // Если список укоротился (untake убрал текущий блок) — отматываем на последний.
  useEffect(() => {
    if (!blocks) return;
    if (blocks.length === 0) {
      setCurrentIdx(0);
      return;
    }
    if (currentIdx >= blocks.length) {
      setCurrentIdx(blocks.length - 1);
    }
  }, [blocks, currentIdx]);

  // Пробрасываем активный блок в App.
  useEffect(() => {
    if (!blocks || !positionInitialized) return;
    if (blocks.length === 0) {
      onPositionChange(null);
      return;
    }
    const current = blocks[currentIdx];
    if (current) onPositionChange(current.block_id);
  }, [blocks, currentIdx, positionInitialized, onPositionChange]);

  const todayPct =
    stats && stats.today_total > 0
      ? Math.round((stats.today_completed / stats.today_total) * 100)
      : 0;

  const total = blocks?.length ?? 0;
  const current = blocks && total > 0 ? blocks[currentIdx] : null;
  const isFirst = currentIdx === 0;
  const isLast = currentIdx === total - 1;
  const isEmpty = blocks !== null && total === 0;

  const onBackClick = () => {
    if (isFirst) return;
    haptic.selectionChanged();
    setCurrentIdx(currentIdx - 1);
  };

  const onNextClick = () => {
    haptic.selectionChanged();
    if (isLast) {
      haptic.impact("light");
      onBack();
      return;
    }
    setCurrentIdx(currentIdx + 1);
  };

  // Снять блок с Дневных (only для не-required, с подтверждением).
  const onBlockDzToggle = async (block: HomeworkBlock, checked: boolean) => {
    if (block.required) return; // required снять нельзя
    if (checked) return; // в Daily чекбокс уже стоит, постановка не нужна
    const ok = await showConfirm("Убрать блок из Дневных?");
    if (!ok) return;
    haptic.selectionChanged();
    try {
      await api<void>("/homework/untake-optional", {
        method: "POST",
        body: JSON.stringify({ block_id: block.block_id }),
      });
      onBlockUntake(block.block_id);
    } catch (e) {
      haptic("error");
      console.warn("block untake failed:", (e as Error).message);
    }
  };

  return (
    <div
      className={`screen screen-with-sticky-header${
        isEmpty ? "" : " screen-with-bottombar"
      }`}
    >
      <header className="lesson-header-sticky">
        <h1>Дневные</h1>
        {/* Резервируем строку под subtitle всегда — чтобы header не «подрастал»
            когда подтянутся stats/blocks. */}
        <p className="subtitle">
          {stats && total > 0 ? (
            <>
              Стрик: {stats.streak_current}{" "}
              {stats.streak_current === 1 ? "день" : "дней"}
              {stats.streak_max > stats.streak_current && (
                <> · макс {stats.streak_max}</>
              )}
              {" · "}
              Сделано: {stats.today_completed} / {stats.today_total}
              {stats.today_total > 0 && <> ({todayPct}%)</>}
              {" · "}
              Блок {currentIdx + 1} из {total}
            </>
          ) : stats && isEmpty ? (
            <>
              Стрик: {stats.streak_current}{" "}
              {stats.streak_current === 1 ? "день" : "дней"}
              {stats.streak_max > stats.streak_current && (
                <> · макс {stats.streak_max}</>
              )}
            </>
          ) : (
            "\u00A0"
          )}
        </p>
      </header>

      {error && <div className="error-card">{error}</div>}

      {!blocks && !error && <div className="card">Загрузка…</div>}

      {isEmpty && (
        <div className="card">
          На сегодня Дневных нет. <span aria-hidden="true">🎉</span>
          <br />
          Пройди уроки — блоки с пометкой «Дневные» попадут сюда автоматически,
          а другие можно взять руками.
        </div>
      )}

      {current && (
        <HomeworkBlockCard
          key={current.block_id}
          block={current}
          onCounterChanged={updateExerciseProgress}
          onBlockDzToggle={onBlockDzToggle}
        />
      )}

      {!isEmpty && blocks && (
        <div className="lesson-bottom-bar">
          <button
            type="button"
            className="lesson-nav-btn lesson-nav-btn-secondary"
            onClick={onBackClick}
            disabled={isFirst}
          >
            ← Назад
          </button>
          <button
            type="button"
            className="lesson-nav-btn lesson-nav-btn-primary"
            onClick={onNextClick}
          >
            {isLast ? "Завершить" : "Далее →"}
          </button>
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// HomeworkBlockCard — рендер блока целиком в контексте Дневных. Структура та же,
// что в Lesson.tsx, но exercise-карточки бьют в /homework/complete +
// /homework/reset-progress (per-exercise), а чекбокс «Дневные» в шапке снимает
// весь блок с Дневных (если он optional).
// ----------------------------------------------------------------------------

type HomeworkBlockCardProps = {
  block: HomeworkBlock;
  onCounterChanged: (blockId: string, itemIndex: number, newCount: number) => void;
  onBlockDzToggle: (block: HomeworkBlock, checked: boolean) => void;
};

function HomeworkBlockCard({
  block,
  onCounterChanged,
  onBlockDzToggle,
}: HomeworkBlockCardProps) {
  const handleExerciseComplete = async (itemIndex: number) => {
    const res = await api<HomeworkComplete>("/homework/complete", {
      method: "POST",
      body: JSON.stringify({ block_id: block.block_id, item_index: itemIndex }),
    });
    onCounterChanged(block.block_id, itemIndex, res.completed_today);
  };

  const handleExerciseReset = async (itemIndex: number) => {
    const res = await api<HomeworkComplete>("/homework/reset-progress", {
      method: "POST",
      body: JSON.stringify({ block_id: block.block_id, item_index: itemIndex }),
    });
    onCounterChanged(block.block_id, itemIndex, res.completed_today);
  };

  const lessonLabel = `${block.section_title} › ${block.lesson_title}`;

  return (
    <article className="block-card">
      <div className="block-item-separated block-item-separated-first">
        <div className="exercise-lesson-meta">{lessonLabel}</div>
        <h2 className="block-title">{block.title}</h2>
        <div className="block-dz-row">
          <label className="exercise-dz-checkbox">
            <input
              type="checkbox"
              checked
              disabled={block.required}
              onChange={(e) => onBlockDzToggle(block, e.target.checked)}
            />
            <span>Дневные</span>
          </label>
        </div>
      </div>
      {block.items.map((item, i) => {
        // user_video_record по логике backend в Дневные не попадает, но если
        // вдруг прилетит — рендерим как невидимый, чтобы не падать на switch.
        if (item.type === "user_video_record") {
          return null;
        }
        // Заголовок блока + чекбокс «Дневные» отрисованы сверху отдельным блоком.
        // exercise рендерится как карточка с собственной рамкой — у неё верхний
        // разделитель лишний.
        const isCardType = item.type === "exercise";
        const itemClass = isCardType
          ? "block-item-separated block-item-card-type"
          : "block-item-separated";
        let inner: React.ReactNode = null;
        switch (item.type) {
          case "paragraph":
            inner = (
              <div className="block-text">
                <ReactMarkdown>{item.text}</ReactMarkdown>
              </div>
            );
            break;
          case "audio":
            inner = (
              <AudioPlayer
                src={item.url}
                title={item.title}
                caption={item.caption ?? null}
              />
            );
            break;
          case "video":
            inner = (
              <VideoPlayer
                url={item.url}
                title={item.title}
                width={item.width ?? null}
                height={item.height ?? null}
                caption={item.caption ?? null}
              />
            );
            break;
          case "image":
            inner = (
              <figure className="block-image">
                <img src={item.url} alt={item.caption ?? ""} />
                {item.caption && (
                  <figcaption className="media-caption">{item.caption}</figcaption>
                )}
              </figure>
            );
            break;
          case "exercise":
            inner = (
              <ExerciseCard
                exercise={item}
                mode="homework"
                count={block.exercise_progress[i] ?? 0}
                onComplete={() => handleExerciseComplete(i)}
                onResetProgress={() => handleExerciseReset(i)}
              />
            );
            break;
        }
        return (
          <div key={i} className={itemClass}>
            {inner}
          </div>
        );
      })}
    </article>
  );
}
